import {Auth} from "@src/pages/auth";
import {useAuth} from "@src/providers/auth-provider";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {Logo} from "@zeg/ui/Logo";
import {useEffect} from "react";
import {z} from "zod";

const searchSchema = z.object({
  desktopAuth: z.string().optional().catch(undefined),
});

function IndexRoute() {
  const navigate = useNavigate();
  const search = useSearch({from: "/"});
  const {user, initData, isProfilesReady} = useAuth();

  // Handle post-authentication redirect
  useEffect(() => {
    if (user && !search.desktopAuth && isProfilesReady) {
      const profiles = initData?.data?.profiles;
      if (profiles && profiles.length > 0) {
        const workspaceSlug = profiles[0].$workspace?.slug;
        if (workspaceSlug) {
          navigate({to: `/${workspaceSlug}`, replace: true});
        }
      } else {
        navigate({to: "/space", replace: true});
      }
    }
  }, [user, search.desktopAuth, isProfilesReady, initData, navigate]);

  return <Auth />;
}

function LoadingComponent() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Logo className="h-10 w-10 animate-pulse" />
    </div>
  );
}

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>) => {
    // Handle both string and boolean values
    const safeSearch = search || {};
    const processed = {
      desktopAuth:
        safeSearch.desktopAuth === true || safeSearch.desktopAuth === "true"
          ? "true"
          : safeSearch.desktopAuth === false ||
              safeSearch.desktopAuth === "false"
            ? "false"
            : (safeSearch.desktopAuth as string) || undefined,
    };
    return searchSchema.parse(processed);
  },
  loader: async ({context, location}) => {
    const {auth} = context;

    // Wait for auth to be ready
    if (!auth || (auth.user && !auth.isProfilesReady)) {
      // Still loading, don't redirect yet
      return null;
    }

    // Check if we should redirect authenticated users
    const search = location.search as {desktopAuth?: string};
    if (auth.user && !search.desktopAuth && auth.isProfilesReady) {
      const profiles = auth.profiles;
      if (profiles && profiles.length > 0) {
        const workspaceSlug = profiles[0].$workspace?.slug;
        if (workspaceSlug) {
          throw redirect({to: `/${workspaceSlug}`, replace: true});
        }
      } else {
        // Profiles loaded but empty
        throw redirect({to: "/space", replace: true});
      }
    }

    return null;
  },
  component: IndexRoute,
  pendingComponent: LoadingComponent,
});
